import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
} from '@mui/material';

import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';

/**
 * Kurzanleitung zur Tastaturbedienung der Fahrterfassung.
 *
 * Erreichbar über das Info-Symbol im Panel-Header und über F1 aus jedem Feld –
 * wer die Belegung vergessen hat, sitzt nicht in den Einstellungen, sondern im
 * Formular mit einem Kunden am Telefon.
 *
 * Siehe docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md, Abschnitt 4.
 *
 * `variant` unterscheidet die beiden Formulare, weil ihre Kürzel abweichen:
 *   'quick' – QuickRideForm (LiveMap-Panel): Alt+S/V, Alt+1…4 Fahrzeugtyp
 *   'full'  – RideForm (großer Dialog): Alt+S/V/D
 * Zwischenstopps (Alt+Z / Alt+Entf / Alt+↑↓) gibt es in beiden.
 * Vorher zeigte der große Dialog Alt+1…4 an, die dort nichts tun, und
 * verschwieg Alt+D.
 */

// Tastenkappe – hebt die Taste vom Fließtext ab.
const Key = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-block',
      px: 0.75,
      py: 0.15,
      mx: 0.15,
      fontFamily: 'inherit',
      fontSize: '0.78rem',
      fontWeight: 600,
      lineHeight: 1.6,
      color: 'text.primary',
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 0.75,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, auto) 1fr',
        columnGap: 2,
        rowGap: 0.9,
        alignItems: 'baseline',
      }}
    >
      {children}
    </Box>
  </Box>
);

const Row = ({ keys, children }) => (
  <>
    <Box sx={{ whiteSpace: 'nowrap' }}>{keys}</Box>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </>
);

const DispatchInputHelpDialog = ({ open, onClose, variant = 'quick' }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>Fahrt per Tastatur erfassen</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Die Erfassung kommt vollständig ohne Maus aus – gedacht für die Aufnahme
        während des Telefonats.
      </Typography>

      <Section title="Durch das Formular bewegen">
        <Row keys={<Key>Enter</Key>}>Zum nächsten Feld</Row>
        <Row
          keys={
            <>
              <Key>Tab</Key> / <Key>Shift</Key>+<Key>Tab</Key>
            </>
          }
        >
          Vorwärts und rückwärts
        </Row>
        <Row
          keys={
            <>
              <Key>Leertaste</Key> / <Key>↓</Key>
            </>
          }
        >
          Auswahlmenü öffnen (Fahrzeugtyp, Zuweisung, Vorlage)
        </Row>
      </Section>

      <Divider sx={{ mb: 2.5 }} />

      <Section title="Adresse eingeben">
        <Row keys={<Key>Enter</Key>}>
          Übernimmt den markierten Vorschlag von Google
        </Row>
        <Row keys={<Key>Enter</Key>}>
          Danach: weiter zum nächsten Feld
        </Row>
        <Row keys={<Key>↓</Key>}>
          Vorschlagsliste erneut öffnen, um die Adresse zu präzisieren
        </Row>
      </Section>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: -1 }}>
        Wird eine Straße ohne Hausnummer gewählt, bleibt der Cursor an der
        richtigen Stelle stehen – die Nummer kann direkt weitergetippt werden.
        Eine Adresse ohne Hausnummer ist erlaubt und blockiert das Anlegen nicht.
      </Typography>

      <Divider sx={{ mb: 2.5 }} />

      <Section title="Zwischenstopps">
        <Row
          keys={
            <>
              <Key>Alt</Key>+<Key>Z</Key>
            </>
          }
        >
          Zwischenstopp vor dem Ziel einfügen, Cursor steht direkt darin
        </Row>
        <Row
          keys={
            <>
              <Key>Alt</Key>+<Key>↑</Key> / <Key>Alt</Key>+<Key>↓</Key>
            </>
          }
        >
          Adresse, in der der Cursor steht, eine Position nach oben oder unten
          verschieben – das Ziel ganz nach oben tauscht Start und Ziel
        </Row>
        <Row
          keys={
            <>
              <Key>Alt</Key>+<Key>Entf</Key>
            </>
          }
        >
          Zwischenstopp entfernen, in dem der Cursor steht (Mac ohne Entf-Taste:{' '}
          <Key>fn</Key>+<Key>⌥</Key>+<Key>⌫</Key>)
        </Row>
      </Section>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: -1 }}>
        <Key>Enter</Key> führt der Reihe nach durch Abholung, Zwischenstopps und
        Ziel. Mit der Maus lassen sich die Adressen auch am Griff ⋮⋮ ziehen.
        {variant === 'full' && ' Bei Krankenfahrten und Daueraufträgen gibt es keine Zwischenstopps.'}
      </Typography>

      <Divider sx={{ mb: 2.5 }} />

      <Section title="Direkt setzen">
        {variant === 'full' ? (
          <Row
            keys={
              <>
                <Key>Alt</Key>+<Key>S</Key> / <Key>Alt</Key>+<Key>V</Key> / <Key>Alt</Key>+<Key>D</Key>
              </>
            }
          >
            Sofortfahrt, Vorbestellung oder Dauerauftrag
          </Row>
        ) : (
          <>
            <Row
              keys={
                <>
                  <Key>Alt</Key>+<Key>S</Key> / <Key>Alt</Key>+<Key>V</Key>
                </>
              }
            >
              Sofortfahrt oder Vorbestellung
            </Row>
            {VEHICLE_TYPE_OPTIONS.map((option) => (
              <Row
                key={option.value}
                keys={
                  <>
                    <Key>Alt</Key>+<Key>{option.value}</Key>
                  </>
                }
              >
                {option.label}
              </Row>
            ))}
          </>
        )}
      </Section>

      <Divider sx={{ mb: 2.5 }} />

      <Section title="Abschließen">
        <Row
          keys={
            <>
              <Key>Strg</Key>+<Key>Enter</Key>
            </>
          }
        >
          Fahrt erstellen – aus jedem Feld heraus
        </Row>
        <Row keys={<Key>Esc</Key>}>Erfassung abbrechen</Row>
        <Row keys={<Key>F1</Key>}>Diese Hilfe</Row>
      </Section>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        <strong>Enter legt die Fahrt nicht an.</strong> Das geht nur über{' '}
        <Key>Strg</Key>+<Key>Enter</Key> oder den Button – so kann ein Enter zu
        früh keine unvollständige Fahrt erzeugen. In der Notiz erzeugt{' '}
        <Key>Enter</Key> wie gewohnt einen Zeilenumbruch.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">
        Schließen
      </Button>
    </DialogActions>
  </Dialog>
);

export default DispatchInputHelpDialog;
